import React, { FC, useEffect, useRef, useState } from 'react';
import { ApolloProvider }                         from '@apollo/react-hooks';
import { useAuth0 }                               from '@auth0/auth0-react';

import { CorvalApolloClient, createClient }       from '../../../../store/_apollo';
import { generateAuthHeaderEntry, RouterUtility } from '../../../../util';
import { UseStateHookFunction }                   from '../../../../types';
import { useLogout }                              from '../../../../hooks';
import { useHistory }            from 'react-router';
import { AUTH_ZERO_ERROR_FLAGS } from './AuthZeroProvider';

interface AuthZeroApolloProviderProps {
	
	/* Instance of an Apollo client to modify */
	client: CorvalApolloClient
}

interface AuthZeroApolloContextType {
	
	/* The current token obtained from Auth0 */
	currentToken?: string,
	currentAuthDetails?: object,
	setAuthDetails?: UseStateHookFunction<any>
}

const defaultProps: AuthZeroApolloProviderProps = {
	client: null,
}

const AuthZeroApolloContext = React.createContext<AuthZeroApolloContextType>( null );

/* Creates an updated Apollo client instance that creates requests with Auth0 request headers and stores basic info into a context */
const AuthZeroApolloClient: FC<AuthZeroApolloProviderProps> = ( { client, children } ) => {
	
	const currentAuth                                 = useRef<AuthZeroApolloContextType>( { currentToken: null } ),
	      [ authDetails, setAuthDetails ]             = useState(),
	      history                                     = useHistory(),
	      { getAccessTokenSilently, isAuthenticated } = useAuth0();
	
	useEffect( () => {
		
		if( !isAuthenticated ){
			
			// clear references to token
			currentAuth.current = {
				currentToken:       null,
				currentAuthDetails: null,
			}
			
		} else {
			
			currentAuth.current.currentAuthDetails = authDetails;
		}
		
	}, [ isAuthenticated, authDetails ] )
	
	/* Create a link that gains an access token silently and appends to apollo's request */
	client = createClient( {
		
		request: async ( operation ) => {
			
			// check auth0's state
			try{
				
				let token = await getAccessTokenSilently();
				
				// set / save token
				currentAuth.current.currentToken = token;
				
				// set header
				operation.setContext( {
					headers: {
						...generateAuthHeaderEntry( token )
					}
				} );
				
			} catch( e ) {
				
				if( e.error === AUTH_ZERO_ERROR_FLAGS.LOGIN_REQUIRED ){
					
					// cancel request, logout
					history.push( RouterUtility.getLoginURL( window.location.pathname ) )
				}
				if( e.error === AUTH_ZERO_ERROR_FLAGS.CONSENT_REQUIRED ){
					
					// cancel request, logout
					history.push( RouterUtility.getLoginURL( window.location.pathname ) )
				}
				
				throw e;
			}
		}
	} );
	
	return (
		<AuthZeroApolloContext.Provider value={ currentAuth.current }>
			<ApolloProvider client={ client }>
				{ children }
			</ApolloProvider>
		</AuthZeroApolloContext.Provider>
	);
};

AuthZeroApolloClient.defaultProps = defaultProps;

export { AuthZeroApolloClient, AuthZeroApolloContext };
